import dayjs from "dayjs"
import type {
  FocusEventHandler,
  KeyboardEventHandler,
  ChangeEventHandler,
} from "react"
import {
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react"
import { useRefState } from "src/hooks/utils"
import {
  roundForStep,
  clamp,
  getDecimalPlaces,
} from "src/utils"

interface Props<T> {
  min: number;
  max: number;
  inputStep: number;
  sliderStep: number;
  value?: number | null;
  updatedAt?: string;
  onChange: (v: number | T) => void;
  allowEmptyValue?: boolean; // when true then the user can clear the number input and it will be left empty
  emptyValue?: T; // this value will be passed to onChange when the input is cleared, otherwise min will be passed
  toFixed?: boolean; // show a fixed number of decimal places when rendering the string to the user
}

interface ReturnValue {
  inputValue: string;
  sliderValue: number;
  onNumberBlur: FocusEventHandler<HTMLInputElement>;
  onNumberKeyDown: KeyboardEventHandler<HTMLInputElement>;
  onSliderChange: ChangeEventHandler<HTMLInputElement>;
  onNumberChange: ChangeEventHandler<HTMLInputElement>;
}

/*
 * this creates input handlers for a slider and number input
 *
 * the slider input will call "onChange" whenever it's changed
 * the number input will call "onChange" when it is blurred or the user presses enter
 * the number input min and max and rounding will be applied on blur or when the user presses enter
 */
export function useSliderInputProps<T>(props: Props<T>): ReturnValue {
  const {
    min,
    max,
    inputStep,
    sliderStep,
    value,
    onChange,
    updatedAt,
    allowEmptyValue,
    toFixed,
    emptyValue,
  } = props

  const numberToString = useCallback(
    (n: number) => {
      if (toFixed) {
        const decimalPlaces = getDecimalPlaces(inputStep)
        return n.toFixed(decimalPlaces)
      }

      return n.toString()
    },
    [
      toFixed,
      inputStep,
    ],
  )

  const [
    currentValue,
    setCurrentValue,
    currentValueRef,
  ] = useRefState<string>(
    typeof value === "number"
      ? numberToString(roundForStep(
        value,
        inputStep,
      ))
      : allowEmptyValue ? "" : numberToString(min),
  )

  const editedAtRef = useRef<string>("")

  useEffect(
    () => {
      if (!updatedAt || !editedAtRef.current || dayjs(updatedAt).isAfter(editedAtRef.current)) {
        if (typeof value === "number") {
          // don't replace an empty value with 0 if allowEmptyValue is true
          if (value === 0 && allowEmptyValue && !currentValueRef.current) {
            return
          }

          setCurrentValue(numberToString(roundForStep(
            value,
            inputStep,
          )))
        } else {
          setCurrentValue(
            allowEmptyValue ? "" : numberToString(min),
          )
        }
      }
    },
    [
      value,
      updatedAt,
      editedAtRef,
      setCurrentValue,
      inputStep,
      numberToString,
      min,
      allowEmptyValue,
      currentValueRef,
    ],
  )

  const onSliderChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const newValue = Number(e.target.value)
      setCurrentValue(numberToString(newValue))
      onChange(newValue)
      editedAtRef.current = dayjs().toISOString()
    },
    [
      numberToString,
      setCurrentValue,
      editedAtRef,
      onChange,
    ],
  )

  const onNumberChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const newValue = e.target.value
      setCurrentValue(newValue)
    },
    [setCurrentValue],
  )

  const clampNumberInputValue = useCallback(
    (target: HTMLInputElement) => {
      const newValue = clamp(
        roundForStep(
          Number(currentValue),
          inputStep,
        ),
        min,
        max,
      )

      const newStringValue = currentValue === ""
        ? (allowEmptyValue ? "" : numberToString(min))
        : numberToString(newValue)

      setCurrentValue(newStringValue)
      target.value = newStringValue // this clears trailing decimal places
      onChange(
        newStringValue === "" && emptyValue !== undefined
          ? emptyValue
          : newValue,
      )

      editedAtRef.current = dayjs().toISOString()
    },
    [
      min,
      max,
      inputStep,
      currentValue,
      setCurrentValue,
      onChange,
      numberToString,
      editedAtRef,
      allowEmptyValue,
      emptyValue,
    ],
  )

  const onNumberBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
    (e) => {
      clampNumberInputValue(e.currentTarget)
    },
    [clampNumberInputValue],
  )

  const onNumberKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (e.key === "Enter") {
        clampNumberInputValue(e.currentTarget)
      }
    },
    [clampNumberInputValue],
  )

  const sliderValue = roundForStep(
    Number(currentValue || min),
    sliderStep,
  )

  return useMemo(
    () => ({
      inputValue: currentValue,
      sliderValue,
      onSliderChange,
      onNumberChange,
      onNumberKeyDown,
      onNumberBlur,
    }),
    [
      currentValue,
      sliderValue,
      onSliderChange,
      onNumberChange,
      onNumberKeyDown,
      onNumberBlur,
    ],
  )
}
